/**
 * StatusFilterButtons component for filtering intakes by status.
 *
 * Displays a set of filter buttons for visible intake statuses (In Progress,
 * Sent for Signature, Chase, Signed) with optional status counts. Clicking a
 * button filters the intake list to show only intakes with that status while
 * preserving other filters (firm, creator, etc.) from the table headers.
 * Clicking an active filter when it's the only status filter clears the status
 * filter but preserves other filters.
 *
 * Currently used for intake filtering. Note: StatusPill (the visual status
 * indicator) is used across both intake and matter pages, but this filter
 * component is specific to intakes.
 *
 * @module StatusFilterButtons
 */

import { AlertTriangle } from 'lucide-react';
import React, { useMemo } from 'react';

import { humanizeStatus } from '@/components/common/DataTable/StatusPill';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';

import type {
  PaginationActions,
  PaginationState,
} from '../../../hooks/usePagination';
import {
  IntakeStatus,
  IntakeStatusCount,
  IntakeStatusValue,
} from '../../../services/types/intake-types';

// Visible status pills (main filters)
// Note: 'scheduled' and 'unconverted' are pseudo-statuses (not in IntakeStatus enum)
// for filtering non-status boolean views.
const VISIBLE_STATUSES: readonly string[] = [
  IntakeStatus.IN_PROGRESS,
  IntakeStatus.SENT_FOR_SIGNATURE,
  IntakeStatus.CHASE,
  'scheduled',
  IntakeStatus.ESCALATED,
  IntakeStatus.SIGNED,
] as const;

// Hidden statuses (moved to overflow menu)
export const HIDDEN_STATUSES: readonly IntakeStatusValue[] = [
  IntakeStatus.REJECTED,
  IntakeStatus.ARCHIVED,
  IntakeStatus.LOST,
] as const;

interface StatusFilterButtonsProps {
  /** Current pagination state including active filters */
  paginationState: PaginationState;
  /** Actions for updating pagination state and filters */
  paginationActions: PaginationActions;
  /** Optional array of status counts to display badges on filter buttons */
  statusCounts?: IntakeStatusCount[];
  /** Number of auto chases needing attention (PC responded) */
  needsAttentionCount?: number;
}

/**
 * StatusFilterButtons component for filtering intakes by status.
 *
 * @param props - Component props
 * @returns Filter buttons for intake statuses
 */
export const StatusFilterButtons: React.FC<StatusFilterButtonsProps> = ({
  paginationState,
  paginationActions,
  statusCounts = [],
  needsAttentionCount = 0,
}) => {
  // Get current status filter from URL
  const statusFilter = useMemo(() => {
    const statuses: string[] = [];
    const { status } = paginationState.filters;
    if (status) {
      const raw = Array.isArray(status) ? status : [status];
      statuses.push(...raw.filter((s): s is string => typeof s === 'string'));
    }
    // Treat is_scheduled filter as the 'scheduled' pseudo-status
    if (paginationState.filters.is_scheduled === 'true') {
      statuses.push('scheduled');
    }
    // Treat unconverted filter as the 'unconverted' pseudo-status
    if (paginationState.filters.unconverted === 'true') {
      statuses.push('unconverted');
    }
    return statuses;
  }, [paginationState.filters]);

  // Create a lookup map for status counts
  const countMap = useMemo(() => {
    const map: Record<string, number> = {};
    statusCounts.forEach((item) => {
      map[item.status] = item.count;
    });
    return map;
  }, [statusCounts]);

  const handleStatusToggle = (status: string) => {
    if (status === 'scheduled') {
      // Toggle the is_scheduled filter instead of the status filter
      const isActive = paginationState.filters.is_scheduled === 'true';
      const newFilters = { ...paginationState.filters };
      delete newFilters.chase_today; // Clear conflicting metric filter
      if (isActive) {
        delete newFilters.is_scheduled;
      } else {
        newFilters.is_scheduled = 'true';
      }
      paginationActions.setFilters(newFilters);
      return;
    }

    if (status === 'unconverted') {
      // Toggle the unconverted filter instead of the status filter
      const isActive = paginationState.filters.unconverted === 'true';
      const newFilters = { ...paginationState.filters };
      delete newFilters.chase_today; // Clear conflicting metric filter
      if (isActive) {
        delete newFilters.unconverted;
      } else {
        newFilters.unconverted = 'true';
        // Avoid combining unconverted with a conflicting status filter
        delete newFilters.status;
      }
      paginationActions.setFilters(newFilters);
      return;
    }

    const currentStatuses = [...statusFilter].filter(
      (s) => s !== 'scheduled' && s !== 'unconverted',
    );
    const isCurrentlyActive = currentStatuses.includes(status);

    if (isCurrentlyActive && currentStatuses.length === 1) {
      // If clicking the only active status, clear it but keep other filters
      const { status: _, chase_today: _ct2, ...otherFilters } = paginationState.filters;
      paginationActions.setFilters(otherFilters);
    } else {
      // Single-select: update status while preserving other filters
      // Clear metric pseudo-filters that conflict with a regular status
      const { unconverted: _u, chase_today: _ct, ...rest } = paginationState.filters;
      paginationActions.setFilters({ ...rest, status });
    }
  };

  return (
    <TooltipProvider delayDuration={300}>
      <div className="flex flex-wrap gap-2">
        {VISIBLE_STATUSES.map((status) => {
          const isActive = statusFilter.includes(status);
          const count = countMap[status];
          const showNeedsAttention =
            status === IntakeStatus.CHASE && needsAttentionCount > 0;

          return (
            <button
              key={status}
              type="button"
              onClick={() => handleStatusToggle(status)}
              className={`
                ${isActive ? 'bg-indigo-50 border-indigo-200 dark:bg-indigo-950 dark:border-indigo-800' : 'bg-white border-gray-300 dark:bg-zinc-900 dark:border-zinc-700'}
                text-black dark:text-white px-3 py-2 rounded-lg text-xs font-semibold whitespace-nowrap
                border transition-colors duration-200
                hover:bg-gray-50 dark:hover:bg-zinc-800
                focus:outline-none
                flex items-center gap-2
              `.trim()}
            >
              <span>{humanizeStatus(status)}</span>
              {count !== undefined && (
                <span className="inline-flex items-center justify-center px-2.5 py-0.5 rounded text-xs font-semibold bg-indigo-100 text-indigo-600 dark:bg-indigo-900 dark:text-indigo-200 min-w-[24px]">
                  {count}
                </span>
              )}
              {showNeedsAttention && (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-bold bg-amber-100 text-amber-700 dark:bg-amber-900 dark:text-amber-200">
                      <AlertTriangle className="h-3 w-3" />
                      {needsAttentionCount}
                    </span>
                  </TooltipTrigger>
                  <TooltipContent side="bottom">
                    <p className="text-xs font-medium">
                      {needsAttentionCount} auto chase
                      {needsAttentionCount !== 1 ? 's' : ''} need attention
                      (client responded)
                    </p>
                  </TooltipContent>
                </Tooltip>
              )}
            </button>
          );
        })}
      </div>
    </TooltipProvider>
  );
};
